import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash, Send, History, Clock } from "lucide-react";
import { Button, DataTable, DropdownMenu, Badge, Tooltip, confirmDialog } from "@/components/ui";
import { DigestSubscription, DigestSubscriptionServices, DigestFrequency } from "@/services/bedrock";
import { toast } from "sonner";
import moment from "moment-timezone";
import { SendTestDigestDialog } from "./SendTestDigestDialog";

interface SubscriptionSectionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  frequency: DigestFrequency;
  search?: string;
  onEdit: (subscription: DigestSubscription) => void;
  onHistory: (subscription: DigestSubscription) => void;
  onCreate: () => void;
}

export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
  title,
  description,
  icon,
  frequency,
  search,
  onEdit,
  onHistory,
  onCreate,
}) => {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [testSubscription, setTestSubscription] = useState<DigestSubscription | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["digestSubscriptions", frequency, pagination, search],
    queryFn: () =>
      DigestSubscriptionServices.getSubscriptions({
        frequency,
        search,
        active: true,
        page: pagination.page,
        perPage: pagination.perPage,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => DigestSubscriptionServices.deleteSubscription(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["digestSubscriptions"] });
      toast.success("Subscription deleted");
    },
    onError: (err: any) => {
      console.error("Failed to delete subscription", err);
      toast.error("Failed to delete subscription");
    },
  });

  const sendTestMutation = useMutation({
    mutationFn: (vars: { id: number; email: string }) => DigestSubscriptionServices.sendTest(vars.id, vars.email),
    onSuccess: () => {
      toast.success("Test email sent");
      setTestSubscription(null);
    },
    onError: (err: any) => {
      console.error("Failed to send test email", err);
      toast.error("Failed to send test email");
    },
  });

  const handleDelete = async (subscription: DigestSubscription) => {
    const confirmed = await confirmDialog({
      title: "Delete Subscription",
      description: `Are you sure you want to delete this ${subscription.frequency} subscription? This action cannot be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (confirmed) {
      deleteMutation.mutate(subscription.digestSubscriptionId);
    }
  };

  const columns = useMemo<ColumnDef<DigestSubscription>[]>(
    () => [
      {
        id: "recipients",
        header: "Subscriber",
        cell: ({ row }) => {
          const recipients = row.original.digestRecipients || [];
          if (recipients.length === 0) return <span className="text-slate-400 italic text-xs">No recipients</span>;
          const owner = recipients.find(r => r.isOwner);
          const primary = owner || recipients[0];
          const additionalCount = recipients.filter(r => !r.isOwner).length;
          return (
            <div className="flex items-center gap-2 max-w-[200px]">
              <span className="truncate text-xs font-medium text-slate-700" title={primary.email}>
                {primary.email}
              </span>
              {additionalCount > 0 && (
                <Badge
                  variant="secondary"
                  className="px-1.5 py-0 text-[10px] h-4 bg-slate-100 text-slate-600 border-none shrink-0"
                >
                  +{additionalCount}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        id: "configuration",
        header: "Report Type",
        cell: ({ row }) => {
          const sub = row.original;
          const isCustomSchedule = sub.frequency === "monthly" && sub.dayOfMonth !== 1;
          const isCustomContent = (sub.enabledComponents?.length ?? 0) > 0;

          if (!isCustomSchedule && !isCustomContent) {
            return (
              <Badge
                variant="outline"
                className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-100 font-bold px-2 py-0 h-5"
              >
                Full Report
              </Badge>
            );
          }

          return (
            <Badge
              variant="outline"
              className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-100 font-bold px-2 py-0 h-5"
            >
              Custom Selection
            </Badge>
          );
        },
      },
      {
        id: "schedule",
        header: "Delivery Schedule",
        cell: ({ row }) => {
          const sub = row.original;
          const timeStr = sub.timeOfDay;
          let formattedTime = "9:00 AM";
          if (timeStr) {
            const m = timeStr.includes("T")
              ? moment.tz(timeStr, "America/Los_Angeles")
              : moment.tz(timeStr, ["HH:mm:ss", "HH:mm"], "America/Los_Angeles");
            formattedTime = m.local().format("h:mm A");
          }

          let scheduleText = "";
          if (sub.frequency === "daily") scheduleText = "Every day";
          else if (sub.frequency === "weekly") {
            const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            scheduleText = `${days[sub.dayOfWeek ?? 1]}s`;
          } else if (sub.frequency === "monthly") {
            const day = sub.dayOfMonth ?? 1;
            const ordinal = (n: number) => {
              const s = ["th", "st", "nd", "rd"];
              const v = n % 100;
              return n + (s[(v - 20) % 10] || s[v] || s[0]);
            };
            scheduleText = `${ordinal(day)} of the month`;
          }

          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-slate-700">{scheduleText}</span>
              <span className="text-[10px] text-slate-500 flex items-center gap-1 font-medium">
                <Clock className="size-2.5 text-slate-400" /> {formattedTime}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "lastSentAt",
        header: "Last Sent",
        cell: ({ getValue }) => {
          const val = getValue() as string;
          if (!val) return <span className="text-slate-400 text-xs">-</span>;
          try {
            return <span className="text-xs text-slate-600 font-medium">{moment(val).fromNow()}</span>;
          } catch {
            return val;
          }
        },
      },
      {
        accessorKey: "nextRunAt",
        header: "Next Run",
        cell: ({ getValue }) => {
          const val = getValue() as string;
          if (!val) return <span className="text-slate-400 text-xs italic">Paused</span>;
          try {
            return (
              <span className="text-xs text-slate-600 font-medium">
                {moment(val).tz(moment.tz.guess()).format("MMM D, h:mm a")}
              </span>
            );
          } catch {
            return val;
          }
        },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex justify-end items-center gap-1">
            <Tooltip>
              <Tooltip.Trigger
                render={props => (
                  <Button
                    {...props}
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onEdit(row.original)}
                    className="text-slate-400 hover:text-slate-900"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              />
              <Tooltip.Content>Edit</Tooltip.Content>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenu.Trigger
                render={props => (
                  <Button {...props} variant="ghost" size="icon-sm" className="text-slate-400 hover:text-slate-900">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                )}
              />
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item onClick={() => setTestSubscription(row.original)}>
                  <Send className="mr-2 h-4 w-4" /> Send Test
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={() => onHistory(row.original)}>
                  <History className="mr-2 h-4 w-4" /> History
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
                <DropdownMenu.Item onClick={() => handleDelete(row.original)} className="text-destructive">
                  <Trash className="mr-2 h-4 w-4" /> Delete
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
        ),
        meta: { intrinsic: true },
      },
    ],
    [sendTestMutation, onEdit, onHistory]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-slate-50 border border-slate-100 shadow-sm">{icon}</div>
            <div className="text-xl font-bold text-slate-900 tracking-tight">{title}</div>
          </div>
          <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">{description}</p>
        </div>
        <Button
          onClick={onCreate}
          size="sm"
          className="h-9 rounded-full px-5 shadow-sm hover:shadow-md transition-all shrink-0"
        >
          New{" "}
          {title
            .slice(0, -1)
            .replace("Digest", "Subscription")
            .replace("Recap", "Subscription")
            .replace("Report", "Subscription")}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition-all hover:border-slate-300">
        <DataTable
          columns={columns}
          data={data?.content || []}
          loading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={data?.pagination?.totalPages}
          rowCount={data?.pagination?.totalCount}
          emptyState={
            <div className="flex flex-col items-center justify-center py-16 text-center gap-3 bg-slate-50/50">
              <div className="size-12 rounded-full bg-slate-100 text-slate-300 flex items-center justify-center italic font-serif text-2xl">
                ?
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-900">
                  {search ? `No subscriptions found matching "${search}"` : `No ${frequency} subscriptions yet.`}
                </p>
                <p className="text-xs text-slate-400">
                  {search
                    ? "Try a different search term or clear the filter."
                    : "Click the button above to schedule your first automated subscription."}
                </p>
              </div>
            </div>
          }
        />
      </div>

      {testSubscription && (
        <SendTestDigestDialog
          open={!!testSubscription}
          onOpenChange={open => !open && setTestSubscription(null)}
          onConfirm={email => sendTestMutation.mutate({ id: testSubscription.digestSubscriptionId, email })}
          loading={sendTestMutation.isPending}
          defaultEmail={
            testSubscription.digestRecipients?.find(r => r.isOwner)?.email || testSubscription.ownerEmail || ""
          }
        />
      )}
    </div>
  );
};
